import React, { useState } from "react";
import { useQuery } from "@apollo/client";
import { Alert, Box, Chip, Link, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import { describeError } from "../../common/rateLimit";
import { Table } from "../../common/table/Table";
import { config } from "../../config";
import { formatDay, formatMoment } from "../crm/contactDetail/contactDetail";
import { GET_POINT_HISTORY } from "../pointHistory/pointHistory.queries";
import { USER_RECOMMENDATIONS, USER_REFERRALS, USER_REVIEWS, USER_WHATSAPP } from "./userDetails.queries";
import { whatsAppDetailColumns, whatsAppDirectionColumn } from "../whatsapp/whatsAppMessageColumns";
import { communityName, humanize, initiativeTitle } from "./userDetailsModel";

const noSort = { skipSorting: true };

/** Page state and the `pagination` variable, the same in every list here. */
const usePage = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(config.rowsPerPage);
  return { page, setPage, rowsPerPage, setRowsPerPage, pagination: { take: rowsPerPage, skip: page * rowsPerPage } };
};

const BusinessLink: React.FC<{ business?: { placeId: string; name: string } | null }> = ({ business }) =>
  business ? (
    <Link component={RouterLink} to={`/businesses/${business.placeId}`} underline="hover" sx={{ fontWeight: 600 }}>
      {business.name}
    </Link>
  ) : null;

const ListError: React.FC<{ error?: unknown; what: string }> = ({ error, what }) =>
  error ? <Alert severity="error">{describeError(error, `Could not load ${what}.`)}</Alert> : null;

type Withdrawn = "all" | "standing" | "withdrawn";

const WITHDRAWN_VARIABLE: Record<Withdrawn, boolean | null> = { all: null, standing: false, withdrawn: true };

/** Everything this person has recommended, newest first, withdrawn ones marked. */
export const UserRecommendations: React.FC<{ userId: number }> = ({ userId }) => {
  const paging = usePage();
  const [filter, setFilter] = useState<Withdrawn>("all");
  const { data, loading, error } = useQuery(USER_RECOMMENDATIONS, {
    variables: { userId, pagination: paging.pagination, withdrawn: WITHDRAWN_VARIABLE[filter] },
    fetchPolicy: "cache-and-network",
  });
  const result = data?.adminUserRecommendations;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h6">Recommended businesses</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={filter}
          aria-label="Which recommendations"
          onChange={(_, next: Withdrawn | null) => {
            if (!next) return;
            setFilter(next);
            paging.setPage(0);
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="standing">Standing</ToggleButton>
          <ToggleButton value="withdrawn">Withdrawn</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <ListError error={error} what="recommendations" />
      <Table
        {...paging}
        loading={loading}
        rows={result?.businessRecommendations ?? []}
        total={result?.total ?? 0}
        columns={[
          {
            ...noSort,
            label: "Business",
            key: "business",
            rowRender: (row) => <BusinessLink business={row.Business} />,
          },
          { ...noSort, label: "Category", key: "category", rowRender: (row) => row.Business?.Category?.category ?? "" },
          {
            ...noSort,
            label: "Address",
            key: "address",
            minWidth: "260px",
            rowRender: (row) => row.Business?.BusinessAddresses?.[0]?.formattedAddress ?? "",
          },
          {
            ...noSort,
            label: "Black-friendly",
            key: "isBlackFriendly",
            minWidth: "120px",
            rowRender: (row) => (row.isBlackFriendly ? "Yes" : "No"),
          },
          {
            ...noSort,
            label: "Recommended",
            key: "createdAt",
            minWidth: "130px",
            rowRender: (row) => formatDay(row.createdAt),
          },
          {
            ...noSort,
            label: "Status",
            key: "status",
            minWidth: "130px",
            rowRender: (row) =>
              row.withDrawnAt ? (
                <Chip size="small" variant="outlined" label={`Withdrawn ${formatDay(row.withDrawnAt)}`} />
              ) : (
                <Chip size="small" variant="outlined" color="success" label="Standing" />
              ),
          },
        ]}
      />
    </Paper>
  );
};

type Direction = "all" | "INBOUND" | "OUTBOUND";

/** What we sent this person on WhatsApp and what they wrote to our number, newest first. */
export const UserWhatsApp: React.FC<{ userId: number }> = ({ userId }) => {
  const paging = usePage();
  const [direction, setDirection] = useState<Direction>("all");
  const { data, loading, error } = useQuery(USER_WHATSAPP, {
    variables: { userId, pagination: paging.pagination, direction: direction === "all" ? null : direction },
    fetchPolicy: "cache-and-network",
  });
  const result = data?.adminUserWhatsAppMessages;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        spacing={1}
        sx={{ mb: 1.5 }}
      >
        <Typography variant="h6">WhatsApp messages</Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={direction}
          aria-label="Which messages"
          onChange={(_, next: Direction | null) => {
            if (!next) return;
            setDirection(next);
            paging.setPage(0);
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="INBOUND">Received</ToggleButton>
          <ToggleButton value="OUTBOUND">Sent</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <ListError error={error} what="WhatsApp messages" />
      <Table
        {...paging}
        loading={loading}
        rows={result?.messages ?? []}
        total={result?.total ?? 0}
        columns={[whatsAppDirectionColumn, ...whatsAppDetailColumns]}
      />
    </Paper>
  );
};

export const UserReviews: React.FC<{ userId: number }> = ({ userId }) => {
  const paging = usePage();
  const { data, loading, error } = useQuery(USER_REVIEWS, {
    variables: { userId, pagination: paging.pagination },
    fetchPolicy: "cache-and-network",
  });
  const result = data?.adminUserReviews;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Reviews
      </Typography>
      <ListError error={error} what="reviews" />
      <Table
        {...paging}
        loading={loading}
        rows={result?.reviews ?? []}
        total={result?.total ?? 0}
        columns={[
          { ...noSort, label: "Business", key: "business", rowRender: (row) => <BusinessLink business={row.Business} /> },
          { ...noSort, label: "Review", key: "content", minWidth: "320px" },
          {
            ...noSort,
            label: "Moderation",
            key: "visible",
            minWidth: "130px",
            rowRender: (row) =>
              row.visible ? (
                <Chip size="small" variant="outlined" color="success" label="Visible" />
              ) : (
                <Chip size="small" variant="outlined" label={row.moderatedAt ? "Hidden" : "Waiting"} />
              ),
          },
          { ...noSort, label: "Written", key: "createdAt", minWidth: "130px", rowRender: (row) => formatDay(row.createdAt) },
        ]}
      />
    </Paper>
  );
};

export const UserReferrals: React.FC<{ userId: number }> = ({ userId }) => {
  const paging = usePage();
  const { data, loading, error } = useQuery(USER_REFERRALS, {
    variables: { userId, pagination: paging.pagination },
    fetchPolicy: "cache-and-network",
  });
  const result = data?.adminUserReferrals;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        People they referred
      </Typography>
      <ListError error={error} what="referrals" />
      <Table
        {...paging}
        loading={loading}
        rows={result?.referrals ?? []}
        total={result?.total ?? 0}
        columns={[
          {
            ...noSort,
            label: "Name",
            key: "name",
            rowRender: (row) => (
              <Link component={RouterLink} to={`/users/${row.userId}`} underline="hover" sx={{ fontWeight: 600 }}>
                {row.name}
              </Link>
            ),
          },
          { ...noSort, label: "Username", key: "username" },
          { ...noSort, label: "Email", key: "email", rowRender: (row) => row.email ?? "" },
          { ...noSort, label: "Phone", key: "phone", minWidth: "140px", rowRender: (row) => row.phone ?? "" },
          {
            ...noSort,
            label: "Account",
            key: "account",
            minWidth: "120px",
            rowRender: (row) => (row.isDeleted ? "Deleted" : row.isVerified ? "Verified" : "Unverified"),
          },
          { ...noSort, label: "Joined", key: "createdAt", minWidth: "130px", rowRender: (row) => formatDay(row.createdAt) },
        ]}
      />
    </Paper>
  );
};

export const UserPoints: React.FC<{ username: string; totalPoints: number }> = ({ username, totalPoints }) => {
  const paging = usePage();
  const { data, loading, error } = useQuery(GET_POINT_HISTORY, {
    variables: {
      pagination: paging.pagination,
      filter: { operator: "AND", filters: [{ field: "username", operator: "equals", value: username }] },
    },
    fetchPolicy: "cache-and-network",
  });
  const result = data?.getPointHistory;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6">Points</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {totalPoints.toLocaleString("en-US")} points in total.
      </Typography>
      <ListError error={error} what="points" />
      <Table
        {...paging}
        loading={loading}
        rows={result?.pointHistory ?? []}
        total={result?.total ?? 0}
        columns={[
          { ...noSort, label: "For", key: "type", rowRender: (row) => humanize(row.type) },
          { ...noSort, label: "Points", key: "amount", textAlign: "right", minWidth: "90px" },
          { ...noSort, label: "When", key: "createdAt", rowRender: (row) => formatMoment(row.createdAt) },
        ]}
      />
    </Paper>
  );
};

/** Short lists, read with the page: a person holds a handful of each. */
export const UserBadges: React.FC<{ user: any }> = ({ user }) => {
  const achievements = (user.UserAchievements ?? []).filter((a: any) => a.isCompleted);

  return (
    <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, alignItems: "start" }}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Badges
        </Typography>
        {user.InitiativeProgress?.length ? (
          <Stack spacing={1} component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {user.InitiativeProgress.map((progress: any) => (
              <Stack
                key={progress.id}
                component="li"
                direction="row"
                justifyContent="space-between"
                spacing={2}
              >
                <Typography variant="body2">{initiativeTitle(progress.Initiative?.InitiativeTranslation)}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {progress.completedAt
                    ? `Earned ${formatDay(progress.completedAt)}`
                    : `${progress.steps} of ${progress.Initiative?.steps ?? "?"} steps`}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No badges started yet.
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Achievements
        </Typography>
        {achievements.length ? (
          <Stack spacing={1} component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
            {achievements.map((achievement: any) => (
              <Stack key={achievement.id} component="li" direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="body2">{achievement.Achievement?.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                  {achievement.Achievement?.points} points · {formatDay(achievement.updatedAt)}
                </Typography>
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No achievements yet.
          </Typography>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Committees
        </Typography>
        {user.CommunityUser?.length ? (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            {user.CommunityUser.map((community: any) => (
              <Chip key={community.streamChannelId} size="small" label={communityName(community.streamChannelId)} />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Not in any committee.
          </Typography>
        )}
      </Paper>
    </Box>
  );
};
